import groupBy from 'lodash/groupBy'

import {
  FinancialWorkspaceRecord,
  PeriodValue,
  RawChildData,
  RawItemData,
  RawRowData
} from 'modules/finance/components/FinancialWorkspaceDataCollection/types'

/** Constant for summarized values category */
const SUMMARIZED_VALUES = 'summarized_values'

/**
 * Transforms raw row data from GraphQL to PeriodValue format
 */
export const transformRowToPeriodValue = (row: RawRowData): PeriodValue => ({
  label: row.columnLabel,
  amountInCents: row.amountInCents,
  amountWithPrecision: row.amountWithPrecision ?? undefined
})

/**
 * Transforms raw child data from GraphQL to FinancialWorkspaceRecord format
 */
export const transformChildToRecord = (child: RawChildData): FinancialWorkspaceRecord => ({
  id: child.label,
  label: child.label,
  hasChildren: false,
  isChild: true,
  periodValues: child.rows.map(transformRowToPeriodValue),
  categoryLabel: child.categoryLabel,
  category: child.category
})

/**
 * Transforms raw item data from GraphQL to FinancialWorkspaceRecord format
 *
 * Note on hasChildren:
 * - If backend provides hasChildren (FinanceFactSummaries), use it
 * - Otherwise, compute from children array (InsightTableItems)
 *
 * This is important because backend's hasChildren may be true even when
 * children array is empty (e.g., when excludeChildrenData is true).
 * For OneDataCollection, we currently only show expanders when actual
 * children data is present (customChildren/lazy loading out of scope).
 */
export const transformItemToRecord = (item: RawItemData): FinancialWorkspaceRecord => ({
  id: item.id,
  label: item.label,
  imageUrl: item.imageUrl,
  active: item.active,
  // Use backend hasChildren if provided, otherwise compute from children array
  hasChildren: item.hasChildren ?? !!(item.children && item.children.length > 0),
  // Mark as child row if specified (for leaf node detection)
  isChild: item.isChild,
  periodValues: item.rows.map(transformRowToPeriodValue),
  children: item.children?.map(transformChildToRecord),
  categoryLabel: item.categoryLabel,
  category: item.category
})

/**
 * Transforms InsightTableItems query response to FinancialWorkspaceRecord array
 */
export const transformInsightTableItems = (
  edges: Array<{ node: RawItemData }>
): FinancialWorkspaceRecord[] => {
  return edges.map((edge) => transformItemToRecord(edge.node))
}

/**
 * Transforms FinanceFactSummaries query response to FinancialWorkspaceRecord array
 */
export const transformFinanceFactSummaries = (
  edges: Array<{ node: RawItemData }>
): FinancialWorkspaceRecord[] => {
  return edges.map((edge) => transformItemToRecord(edge.node))
}

/**
 * Merges comparison data into records by matching IDs
 * Used when hasComparisonDate is true
 *
 * This ensures robust alignment between current and comparison data:
 * 1. Builds a hash map of comparison records by ID for O(1) lookup
 * 2. For each current record, finds matching comparison by ID
 * 3. Sets comparisonValues from the matching comparison record
 *
 * This approach is resilient to:
 * - Different ordering between current and comparison queries
 * - Missing records in either dataset (will have undefined comparisonValues)
 * - Extra records in comparison data (ignored)
 */
export const mergeComparisonDataByIds = (
  records: FinancialWorkspaceRecord[],
  comparisonRecords: FinancialWorkspaceRecord[]
): FinancialWorkspaceRecord[] => {
  // Build a hash map of comparison records by ID for efficient lookup
  const comparisonMap = new Map<string | number, FinancialWorkspaceRecord>()
  comparisonRecords.forEach((record) => {
    comparisonMap.set(record.id, record)
  })

  // Merge comparison data into records by ID matching
  return records.map((record) => {
    const matchingComparison = comparisonMap.get(record.id)

    return {
      ...record,
      comparisonValues: matchingComparison?.periodValues,
      // Also merge children if present
      children: record.children?.map((child) => {
        const matchingComparisonChild = matchingComparison?.children?.find(
          (compChild) => compChild.id === child.id
        )
        return {
          ...child,
          comparisonValues: matchingComparisonChild?.periodValues
        }
      })
    }
  })
}

/**
 * @deprecated Use mergeComparisonDataByIds instead for robust ID-based matching
 * This function uses index-based matching which is fragile
 */
export const mergeComparisonData = (
  records: FinancialWorkspaceRecord[],
  comparisonRecords: FinancialWorkspaceRecord[]
): FinancialWorkspaceRecord[] => {
  return records.map((record, index) => ({
    ...record,
    comparisonValues: comparisonRecords[index]?.periodValues
  }))
}

/**
 * Groups records by their categoryLabel, creating parent rows with aggregated totals
 * and the original records as children.
 *
 * This is used for Payroll/Compensation tables when groupBy: LineItem
 * to group line items by their category (e.g., "Earnings", "Deductions", "Taxes").
 *
 * The logic matches the original regroupedItemsByCategory function but works with
 * FinancialWorkspaceRecord instead of PaginatedTableItem.
 */
export const groupRecordsByCategory = (
  records: FinancialWorkspaceRecord[]
): FinancialWorkspaceRecord[] => {
  // Check if any records have categoryLabel - if not, return as-is
  const hasCategoryLabel = records.some((record) => record.categoryLabel)
  if (!hasCategoryLabel) {
    return records
  }

  // Group records by categoryLabel
  const grouped = groupBy(records, 'categoryLabel')

  return Object.entries(grouped)
    .map(([categoryLabel, groupItems]) => {
      // Check for special categories
      const isUngrouped = groupItems.some((item) => !item.category)
      const isSummarized = groupItems.some((item) => item.category === SUMMARIZED_VALUES)

      // Aggregate period values across all items in the group
      const aggregatedPeriodValues: Record<string, PeriodValue> = {}

      groupItems.forEach((item) => {
        item.periodValues.forEach((pv) => {
          const existing = aggregatedPeriodValues[pv.label] ?? {
            label: pv.label,
            amountInCents: '0',
            amountWithPrecision: undefined
          }

          // For ungrouped or summarized items, show 0
          const newAmount =
            isSummarized || isUngrouped
              ? '0'
              : (Number(existing.amountInCents) + Number(pv.amountInCents)).toString()

          aggregatedPeriodValues[pv.label] = {
            ...existing,
            amountInCents: newAmount
          }
        })
      })

      // Create the group parent record
      const groupRecord: FinancialWorkspaceRecord = {
        id: categoryLabel,
        label: categoryLabel,
        hasChildren: groupItems.length > 0,
        periodValues: Object.values(aggregatedPeriodValues),
        // Original items become children (without categoryLabel to prevent recursion)
        children: groupItems.map((item) => ({
          ...item,
          categoryLabel: undefined,
          category: undefined,
          hasChildren: false,
          isChild: true
        })),
        // Styling flags for special categories
        grey: isUngrouped || isSummarized,
        italic: isSummarized
      }

      return groupRecord
    })
    .sort((a, b) => {
      // Ensure ungrouped and summarized items are placed last
      const aIsUngrouped = a.children?.every((child) => !child.category) ?? false
      const bIsUngrouped = b.children?.every((child) => !child.category) ?? false
      const aIsSummarized =
        a.children?.some((child) => child.category === SUMMARIZED_VALUES) ?? false
      const bIsSummarized =
        b.children?.some((child) => child.category === SUMMARIZED_VALUES) ?? false

      if (bIsUngrouped && aIsSummarized) return 1
      if (aIsUngrouped && bIsSummarized) return -1
      if (aIsUngrouped && !bIsUngrouped) return 1
      if (!aIsUngrouped && bIsUngrouped) return -1
      if (aIsSummarized && !bIsSummarized) return 1
      if (!aIsSummarized && bIsSummarized) return -1

      return 0
    })
}

/**
 * Groups children of each record by their categoryLabel.
 * Used when groupBy is not LineItem (e.g., Employee, LegalEntity) where
 * the children (line items) need to be grouped by category.
 */
export const groupChildrenByCategory = (
  records: FinancialWorkspaceRecord[]
): FinancialWorkspaceRecord[] => {
  return records.map((record) => {
    if (!record.children || record.children.length === 0) {
      return record
    }

    // Group the children by category
    const groupedChildren = groupRecordsByCategory(record.children)

    return {
      ...record,
      children: groupedChildren,
      // Update hasChildren based on grouped children
      hasChildren: groupedChildren.length > 0
    }
  })
}
